/**
 * Resolve the current subject's capability tags from the (globally de-duped,
 * session-cached) capability projection. Used to filter which KPIs a user may
 * see/pin. Fail-open: on any failure we return an empty tag set, and callers
 * treat "no tags" as "show all read-only ops metrics".
 */
import React from 'react';
import { getPluginManifests } from '../../plugins/registry';
import { fetchCapabilityProjection } from '../auth/capabilityProjection';

export interface CapabilityTagsState {
  tags: string[];
  loading: boolean;
  degraded: boolean;
}

export function useCapabilityTags(): CapabilityTagsState {
  const [state, setState] = React.useState<CapabilityTagsState>({
    tags: [],
    loading: true,
    degraded: false,
  });

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const manifests = await getPluginManifests();
        const enabled = manifests.filter((m) => m.enabled !== false);
        const projection = await fetchCapabilityProjection(enabled);
        if (cancelled) return;
        setState({
          tags: Array.isArray(projection.capability_tags) ? projection.capability_tags : [],
          loading: false,
          degraded: !!projection.degraded,
        });
      } catch {
        if (!cancelled) setState({ tags: [], loading: false, degraded: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Respect `prefers-reduced-motion` for optional entrance/live animations. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = React.useState<boolean>(() => {
    try {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    } catch {
      return;
    }
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}
